export type DetectorOptions = {
    // Порог RMS (0..1) для оценки громкости (поднять при ложных срабатываниях на шум).
    threshold: number;
    // Сколько миллисекунд должен говорить пользователь, чтобы считать это речью
    // (отсекает щелчки клавиатуры, кашель, удары по столу).
    speechDurationMs: number;
    // Минимальный интервал между срабатываниями колбэка (типа debounce).
    cooldownMs: number;
    // Эпизод обрывается только если тишина длится непрерывно дольше этого.
    // Защита от микропровалов RMS между слогами в естественной речи.
    silenceGapMs: number;
    // Сколько миллисекунд флаг продолжает висеть после того, как юзер замолчал.
    // Если речь возобновится в течение этого окна — отложенный сброс отменяется.
    hideDelayMs: number;
};

export type DetectorCallbacks = {
    onMutedSpeechChange: (isSpeaking: boolean) => void;
};

export type MutedSpeechDetector = {
    start: (deviceId?: string) => Promise<void>;
    stop: () => void;
    // Взводится, когда тултип перекрыл открытое меню.
    pause: () => void;
    // Продолжает детектирование, когда перекрытия нет.
    resume: () => void;
};

// ~30 замеров RMS в секунду (1000/30 ≈ 33): чаще — лишняя нагрузка на CPU.
const ANALYSIS_INTERVAL_MS = 33;

export function createMutedSpeechDetector(options: DetectorOptions, callbacks: DetectorCallbacks): MutedSpeechDetector {
    let stream: MediaStream | null = null;
    let ctx: AudioContext | null = null;
    let analyser: AnalyserNode | null = null;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    // Троттлинг setInterval в фоне может отличаться в разных браузерах,
    // поэтому подписываемся на visibilitychange, чтобы стопать детектор в фоне.
    let visibilityListener: (() => void) | null = null;
    // Момент начала текущего громкого эпизода (performance.now()). null = эпизода нет.
    let speechStartedAt: number | null = null;
    // Момент последнего тика, на котором RMS превышал порог. Нужен для silenceGapMs:
    // короткие провалы громкости между слогами не должны обрывать эпизод.
    let lastLoudAt = 0;
    // Момент последнего вызова колбэка. Нужен для cooldownMs.
    let lastFiredAt = 0;
    // Момент, в который надо погасить флаг (после задержки hideDelayMs).
    // null = скрытие не запланировано.
    let lingerUntil: number | null = null;
    // Сигнал «start был отменён». Взводится в stop(). Если stop() отработал, пока start()
    // ждал getUserMedia, мы не должны создавать AudioContext и запускать таймер — освободить
    // их уже некому, и будет утечка.
    let cancelled = false;
    // Пока true — детекция на паузе: tick не считает RMS и не дергает колбэк. Аудио-граф
    // при этом жив, поэтому снятие с паузы мгновенно, без повторного getUserMedia.
    let suppressed = false;

    // fftSize константа, поэтому буфер общий на всю жизнь детектора, переживает teardown/setup графа.
    const buf = new Uint8Array(2048);

    // Поднимает аудио-граф поверх существующего stream. Вызывается из start() и при возврате
    // из фона (visible). Когда вкладка скрыта, граф полностью разбирается teardownAudioGraph(),
    // чтобы избежать «протухшего» MediaStreamSource — после долгого suspend() AudioContext
    // возвращается в running, но source перестаёт качать сэмплы из стрима и детектор не работает.
    const setupAudioGraph = () => {
        if (!stream) return;
        try {
            ctx = new AudioContext();
            const source = ctx.createMediaStreamSource(stream);
            analyser = ctx.createAnalyser();
            analyser.fftSize = 2048;
            source.connect(analyser);
        } catch (e) {
            // Напр. превышен лимит AudioContext на страницу (в Chromium — 6): new AudioContext() бросает.
            // Откатываем частично созданное, чтобы не остаться в полусостоянии — граф не поднимется,
            // analyser === null, tick выйдет по !analyser. Стрим тут не трогаем: за него отвечает вызывающий.
            console.error('muted speech detection: не удалось создать AudioContext', e);
            teardownAudioGraph();
        }
    };

    const teardownAudioGraph = () => {
        analyser?.disconnect();
        analyser = null;
        ctx?.close().catch(() => {});
        ctx = null;
    };

    const stop = () => {
        cancelled = true;
        if (visibilityListener) {
            document.removeEventListener('visibilitychange', visibilityListener);
            visibilityListener = null;
        }
        if (intervalId !== null) clearInterval(intervalId);
        intervalId = null;
        teardownAudioGraph();
        stream?.getTracks().forEach((t) => t.stop());
        stream = null;
        speechStartedAt = null;
        lingerUntil = null;
        suppressed = false;
        callbacks.onMutedSpeechChange(false);
    };

    const start = async (deviceId?: string) => {
        stop();
        cancelled = false;

        let localStream: MediaStream;
        try {
            localStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    deviceId: deviceId ? { exact: deviceId } : undefined,
                    echoCancellation: true,
                    noiseSuppression: true,
                    // autoGainControl накручивает громкость тихих звуков → ложные срабатывания
                    autoGainControl: false,
                },
            });
        } catch {
            console.error('muted speech detection error');
            return;
        }

        // проверка нужна, поскольку выше getUserMedia - асинхронный
        if (cancelled) {
            localStream.getTracks().forEach((t) => t.stop());
            return;
        }

        stream = localStream;
        setupAudioGraph();

        // Если граф не поднялся (напр. превышен лимит AudioContext на страницу) — не держим
        // микрофон открытым впустую: глушим только что полученный стрим и выходим.
        if (!analyser) {
            localStream.getTracks().forEach((t) => t.stop());
            stream = null;
            return;
        }

        const tick = () => {
            if (!analyser) return;

            // Детекция на паузе (тултип перекрыл меню) — ничего не считаем и не фаерим.
            if (suppressed) return;

            analyser.getByteTimeDomainData(buf);

            // Считаем RMS — «среднюю громкость» сигнала.
            let sum = 0;
            for (let i = 0; i < buf.length; i++) {
                const v = (buf[i] - 128) / 128;
                sum += v * v;
            }
            const rms = Math.sqrt(sum / buf.length);
            const now = performance.now();

            // Отложенный сброс флага: если linger истёк — гасим подсказку.
            if (lingerUntil !== null && now >= lingerUntil) {
                lingerUntil = null;
                callbacks.onMutedSpeechChange(false);
            }

            const isLoud = rms > options.threshold;
            if (isLoud) {
                lastLoudAt = now;
                if (speechStartedAt === null) {
                    speechStartedAt = now;
                    // Речь возобновилась — отменяем запланированное скрытие.
                    lingerUntil = null;
                    return;
                }

                // Эпизод длится достаточно долго, чтобы считаться речью.
                const spokeLongEnough = now - speechStartedAt >= options.speechDurationMs;
                // С прошлого срабатывания прошло достаточно времени (антиспам коллбэка).
                const cooledDown = now - lastFiredAt >= options.cooldownMs;
                if (spokeLongEnough && cooledDown) {
                    lastFiredAt = now;
                    // speechStartedAt НЕ обнуляем: эпизод должен дожить до настоящей
                    // тишины, чтобы silenceTooLong сбросил флаг в false. Иначе если
                    // юзер замолчит сразу после fire — флаг застрянет в true.
                    callbacks.onMutedSpeechChange(true);
                }

                return;
            }

            // Тишина длится дольше допустимого — закрываем эпизод. Короткие провалы
            // громкости внутри речи (пауза между слогами) сюда не попадают.
            const silenceTooLong = speechStartedAt !== null && now - lastLoudAt > options.silenceGapMs;
            if (silenceTooLong) {
                speechStartedAt = null;
                // Подсказку не гасим сразу — даём ей повисеть hideDelayMs. Если за это
                // время юзер заговорит снова, отложенный сброс отменится в isLoud-ветке.
                lingerUntil = now + options.hideDelayMs;
            }
        };
        // На скрытой вкладке полностью разбираем аудио-граф, на возврате собираем заново.
        // Не пользуемся AudioContext.suspend()/resume(), потому что после долгой паузы
        // MediaStreamSource в Chromium/WebKit перестаёт качать сэмплы из стрима даже после
        // успешного resume — analyser возвращает нули, флаг в сторе не меняется. Свежий
        // граф на каждом возврате эту проблему обходит.
        const syncToVisibility = () => {
            if (document.hidden) {
                if (intervalId !== null) {
                    clearInterval(intervalId);
                    intervalId = null;
                }
                teardownAudioGraph();
            } else {
                if (!ctx) setupAudioGraph();
                if (intervalId === null) {
                    intervalId = setInterval(tick, ANALYSIS_INTERVAL_MS);
                }
            }
        };
        visibilityListener = syncToVisibility;
        document.addEventListener('visibilitychange', visibilityListener);

        // Сразу выравниваем под текущее состояние вкладки: если start() вызван на скрытой вкладке,
        // только что собранный setupAudioGraph() снесётся обратно teardownAudioGraph(), а граф
        // соберётся заново на возврате фокуса.
        syncToVisibility();
    };

    const pause = () => {
        suppressed = true;
    };

    // Сбрасываем эпизод и throttle, поэтому следующая речь задетектируется через
    // speechDurationMs, а не досиживает остаток cooldownMs. «Как в первый раз».
    const resume = () => {
        speechStartedAt = null;
        lastFiredAt = 0;
        lingerUntil = null;
        suppressed = false;
    };

    return { start, stop, pause, resume };
}
